import React, { useMemo, useRef, useState } from 'react';
import { Camera, Save, X } from 'lucide-react';
import { Colonia } from '../../types/colonia';
import { fileToCompressedDataUrl, TURMAS } from '../formularioParts/utils';
import { updateAdminInscricao } from './adminData';

const TAMANHOS_CAMISA = ['04', '06', '08', '10', '12', '14', '16', 'P', 'M', 'G', 'GG'];
const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

interface EditFormState {
  fotoUrl: string;
  fotoUrlRetirada: string;
  nomeCompleto: string;
  dataNascimento: string;
  idade: string;
  turma: string;
  tamanhoCamisa: string;
  associadoUba: boolean;
  codigoAssociado: string;
  nomeMae: string;
  telefoneMae: string;
  nomePai: string;
  telefonePai: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  nomeRetirada: string;
  parentescoRetirada: string;
  sabeNadar: boolean;
  autorizacaoPiscina: string;
  possuiAlergia: boolean;
  alergias: string;
  possuiDoenca: boolean;
  doencas: string;
  tomaMedicamento: boolean;
  medicamentos: string;
  medicacaoHiperatividade: boolean;
  medicamentosHiperatividade: string;
  transporteVan: boolean;
  participacaoBingo: boolean;
  gratuito: boolean;
  pago: boolean;
  status: string;
  valorTotal: string;
}

const buildFormState = (inscricao: Colonia): EditFormState => ({
  fotoUrl: inscricao.fotoUrl || '',
  fotoUrlRetirada: inscricao.fotoUrlRetirada || '',
  nomeCompleto: inscricao.nomeCompleto || '',
  dataNascimento: inscricao.dataNascimento || '',
  idade: String(inscricao.idade ?? ''),
  turma: inscricao.turma || '',
  tamanhoCamisa: inscricao.tamanhoCamisa || '',
  associadoUba: Boolean(inscricao.associadoUba),
  codigoAssociado: inscricao.codigoAssociado || '',
  nomeMae: inscricao.nomeMae || '',
  telefoneMae: inscricao.telefoneMae || '',
  nomePai: inscricao.nomePai || '',
  telefonePai: inscricao.telefonePai || '',
  cep: inscricao.cep || '',
  endereco: inscricao.endereco || '',
  numero: inscricao.numero || '',
  complemento: inscricao.complemento || '',
  bairro: inscricao.bairro || '',
  cidade: inscricao.cidade || '',
  uf: inscricao.uf || '',
  nomeRetirada: inscricao.nomeRetirada || '',
  parentescoRetirada: inscricao.parentescoRetirada || '',
  sabeNadar: Boolean(inscricao.sabeNadar),
  autorizacaoPiscina: inscricao.autorizacaoPiscina || 'autorizado',
  possuiAlergia: Boolean(inscricao.possuiAlergia),
  alergias: inscricao.alergias || '',
  possuiDoenca: Boolean(inscricao.possuiDoenca),
  doencas: inscricao.doencas || '',
  tomaMedicamento: Boolean(inscricao.tomaMedicamento),
  medicamentos: inscricao.medicamentos || '',
  medicacaoHiperatividade: Boolean(inscricao.medicacaoHiperatividade),
  medicamentosHiperatividade: inscricao.medicamentosHiperatividade || '',
  transporteVan: Boolean(inscricao.transporteVan),
  participacaoBingo: Boolean(inscricao.participacaoBingo),
  gratuito: Boolean(inscricao.gratuito),
  pago: Boolean(inscricao.pago),
  status: inscricao.status || 'confirmado',
  valorTotal: String(inscricao.valorTotal ?? 0)
});

const parseCurrencyInput = (value: string) => {
  const parsed = Number(String(value).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : NaN;
};

const buildPatch = (original: Colonia, form: EditFormState): Partial<Colonia> => {
  const patch: Record<string, unknown> = {};

  const setIfChanged = (field: keyof Colonia, next: unknown, current: unknown) => {
    if (next !== current) patch[field] = next;
  };

  setIfChanged('fotoUrl', form.fotoUrl, original.fotoUrl || '');
  setIfChanged('fotoUrlRetirada', form.fotoUrlRetirada, original.fotoUrlRetirada || '');
  setIfChanged('nomeCompleto', form.nomeCompleto.trim(), original.nomeCompleto || '');
  setIfChanged('dataNascimento', form.dataNascimento.trim(), original.dataNascimento || '');
  setIfChanged('turma', form.turma.trim(), original.turma || '');
  setIfChanged('tamanhoCamisa', form.tamanhoCamisa.trim(), original.tamanhoCamisa || '');
  setIfChanged('associadoUba', form.associadoUba, Boolean(original.associadoUba));
  setIfChanged('codigoAssociado', form.codigoAssociado.trim(), original.codigoAssociado || '');
  setIfChanged('nomeMae', form.nomeMae.trim(), original.nomeMae || '');
  setIfChanged('telefoneMae', form.telefoneMae.trim(), original.telefoneMae || '');
  setIfChanged('nomePai', form.nomePai.trim(), original.nomePai || '');
  setIfChanged('telefonePai', form.telefonePai.trim(), original.telefonePai || '');
  setIfChanged('cep', form.cep.trim(), original.cep || '');
  setIfChanged('endereco', form.endereco.trim(), original.endereco || '');
  setIfChanged('numero', form.numero.trim(), original.numero || '');
  setIfChanged('complemento', form.complemento.trim(), original.complemento || '');
  setIfChanged('bairro', form.bairro.trim(), original.bairro || '');
  setIfChanged('cidade', form.cidade.trim(), original.cidade || '');
  setIfChanged('uf', form.uf.trim(), original.uf || '');
  setIfChanged('nomeRetirada', form.nomeRetirada.trim(), original.nomeRetirada || '');
  setIfChanged('parentescoRetirada', form.parentescoRetirada.trim(), original.parentescoRetirada || '');
  setIfChanged('sabeNadar', form.sabeNadar, Boolean(original.sabeNadar));
  setIfChanged('autorizacaoPiscina', form.autorizacaoPiscina, original.autorizacaoPiscina || 'autorizado');
  setIfChanged('possuiAlergia', form.possuiAlergia, Boolean(original.possuiAlergia));
  setIfChanged('alergias', form.possuiAlergia ? form.alergias.trim() : '', original.alergias || '');
  setIfChanged('possuiDoenca', form.possuiDoenca, Boolean(original.possuiDoenca));
  setIfChanged('doencas', form.possuiDoenca ? form.doencas.trim() : '', original.doencas || '');
  setIfChanged('tomaMedicamento', form.tomaMedicamento, Boolean(original.tomaMedicamento));
  setIfChanged('medicamentos', form.tomaMedicamento ? form.medicamentos.trim() : '', original.medicamentos || '');
  setIfChanged('medicacaoHiperatividade', form.medicacaoHiperatividade, Boolean(original.medicacaoHiperatividade));
  setIfChanged(
    'medicamentosHiperatividade',
    form.medicacaoHiperatividade ? form.medicamentosHiperatividade.trim() : '',
    original.medicamentosHiperatividade || ''
  );
  setIfChanged('transporteVan', form.transporteVan, Boolean(original.transporteVan));
  setIfChanged('participacaoBingo', form.participacaoBingo, Boolean(original.participacaoBingo));
  setIfChanged('gratuito', form.gratuito, Boolean(original.gratuito));
  setIfChanged('pago', form.pago, Boolean(original.pago));
  setIfChanged('status', form.status, original.status || 'confirmado');

  const idade = Number(form.idade);
  if (Number.isFinite(idade) && idade !== original.idade) patch.idade = Math.round(idade);

  const valorTotal = parseCurrencyInput(form.valorTotal);
  if (Number.isFinite(valorTotal) && valorTotal !== original.valorTotal) patch.valorTotal = valorTotal;

  return patch as Partial<Colonia>;
};

const Field = ({
  label,
  children,
  span
}: {
  label: string;
  children: React.ReactNode;
  span?: boolean;
}) => (
  <label className={`registration-edit-field${span ? ' span' : ''}`}>
    <span>{label}</span>
    {children}
  </label>
);

const PhotoPicker = ({
  label,
  value,
  changed,
  onChange,
  onError
}: {
  label: string;
  value: string;
  changed: boolean;
  onChange: (dataUrl: string) => void;
  onError: (message: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setLoading(true);
      onChange(await fileToCompressedDataUrl(file));
    } catch {
      onError(`Não foi possível ler a imagem de "${label}". Tente outro arquivo.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-edit-photo">
      <div className="registration-edit-photo-frame">
        {value ? <img src={value} alt={label} /> : <span>{label.slice(0, 2)}</span>}
        {changed && <em>Nova</em>}
      </div>
      <span>{label}</span>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={loading}>
        <Camera size={13} />
        {loading ? 'Lendo...' : 'Trocar'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(event) => void handleFile(event)}
      />
    </div>
  );
};

const Toggle = ({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) => (
  <label className="registration-edit-toggle">
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>
);

interface RegistrationEditModalProps {
  inscricao: Colonia;
  onClose: () => void;
  onSaved: (updated: Colonia) => void;
}

const RegistrationEditModal: React.FC<RegistrationEditModalProps> = ({ inscricao, onClose, onSaved }) => {
  const [form, setForm] = useState<EditFormState>(() => buildFormState(inscricao));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const patch = useMemo(() => buildPatch(inscricao, form), [form, inscricao]);
  const hasChanges = Object.keys(patch).length > 0;

  const update = <K extends keyof EditFormState>(field: K, value: EditFormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const validate = () => {
    if (!form.nomeCompleto.trim()) return 'Informe o nome da criança.';
    if (!form.nomeMae.trim()) return 'Informe o nome do responsável.';
    if (!form.telefoneMae.trim()) return 'Informe o WhatsApp do responsável.';
    const idade = Number(form.idade);
    if (!Number.isFinite(idade) || idade < 0) return 'Informe uma idade válida.';
    const valorTotal = parseCurrencyInput(form.valorTotal);
    if (!Number.isFinite(valorTotal) || valorTotal < 0) return 'Informe um valor total válido.';
    return '';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!hasChanges) {
      onClose();
      return;
    }
    if (!inscricao.id) {
      setError('Inscrição sem identificador válido.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const updated = await updateAdminInscricao(inscricao.id, patch);
      onSaved(updated);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar as alterações.');
    } finally {
      setSaving(false);
    }
  };

  const camisaOptions = TAMANHOS_CAMISA.includes(form.tamanhoCamisa) || !form.tamanhoCamisa
    ? TAMANHOS_CAMISA
    : [form.tamanhoCamisa, ...TAMANHOS_CAMISA];
  const turmaOptions = TURMAS.includes(form.turma) || !form.turma ? TURMAS : [form.turma, ...TURMAS];

  return (
    <div className="registration-modal-backdrop" role="presentation" onClick={onClose}>
      <form
        className="registration-details-modal registration-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="registration-edit-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="registration-details-header">
          <div>
            <h3 id="registration-edit-title">Editar inscrição</h3>
            <p>{inscricao.nomeCompleto}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
        </div>

        <div className="registration-details-body registration-edit-body">
          <section className="registration-edit-photos">
            <h4>Fotos</h4>
            <div className="registration-edit-photo-row">
              <PhotoPicker
                label="Criança"
                value={form.fotoUrl}
                changed={form.fotoUrl !== (inscricao.fotoUrl || '')}
                onChange={(dataUrl) => update('fotoUrl', dataUrl)}
                onError={setError}
              />
              <PhotoPicker
                label="Retirada"
                value={form.fotoUrlRetirada}
                changed={form.fotoUrlRetirada !== (inscricao.fotoUrlRetirada || '')}
                onChange={(dataUrl) => update('fotoUrlRetirada', dataUrl)}
                onError={setError}
              />
            </div>
          </section>

          <section>
            <h4>Criança</h4>
            <div className="registration-edit-grid">
              <Field label="Nome completo *" span>
                <input value={form.nomeCompleto} onChange={(event) => update('nomeCompleto', event.target.value)} required />
              </Field>
              <Field label="Nascimento">
                <input value={form.dataNascimento} onChange={(event) => update('dataNascimento', event.target.value)} placeholder="dd/mm/aaaa" />
              </Field>
              <Field label="Idade">
                <input type="number" min={0} max={17} value={form.idade} onChange={(event) => update('idade', event.target.value)} />
              </Field>
              <Field label="Turma">
                <select value={form.turma} onChange={(event) => update('turma', event.target.value)}>
                  <option value="">Sem turma</option>
                  {turmaOptions.map((turma) => (
                    <option key={turma} value={turma}>{turma}</option>
                  ))}
                </select>
              </Field>
              <Field label="Camisa">
                <select value={form.tamanhoCamisa} onChange={(event) => update('tamanhoCamisa', event.target.value)}>
                  <option value="">-</option>
                  {camisaOptions.map((tamanho) => (
                    <option key={tamanho} value={tamanho}>{tamanho}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="registration-edit-toggles">
              <Toggle label="Associado UBA" checked={form.associadoUba} onChange={(value) => update('associadoUba', value)} />
            </div>
            {form.associadoUba && (
              <div className="registration-edit-grid">
                <Field label="Código do associado" span>
                  <input value={form.codigoAssociado} onChange={(event) => update('codigoAssociado', event.target.value)} />
                </Field>
              </div>
            )}
          </section>

          <section>
            <h4>Contato</h4>
            <div className="registration-edit-grid">
              <Field label="Responsável *">
                <input value={form.nomeMae} onChange={(event) => update('nomeMae', event.target.value)} required />
              </Field>
              <Field label="WhatsApp *">
                <input value={form.telefoneMae} onChange={(event) => update('telefoneMae', event.target.value)} required />
              </Field>
              <Field label="Pai">
                <input value={form.nomePai} onChange={(event) => update('nomePai', event.target.value)} />
              </Field>
              <Field label="Tel. pai">
                <input value={form.telefonePai} onChange={(event) => update('telefonePai', event.target.value)} />
              </Field>
            </div>
          </section>

          <section>
            <h4>Endereço</h4>
            <div className="registration-edit-grid">
              <Field label="CEP">
                <input value={form.cep} onChange={(event) => update('cep', event.target.value)} />
              </Field>
              <Field label="Rua">
                <input value={form.endereco} onChange={(event) => update('endereco', event.target.value)} />
              </Field>
              <Field label="Número">
                <input value={form.numero} onChange={(event) => update('numero', event.target.value)} />
              </Field>
              <Field label="Complemento">
                <input value={form.complemento} onChange={(event) => update('complemento', event.target.value)} />
              </Field>
              <Field label="Bairro">
                <input value={form.bairro} onChange={(event) => update('bairro', event.target.value)} />
              </Field>
              <Field label="Cidade">
                <input value={form.cidade} onChange={(event) => update('cidade', event.target.value)} />
              </Field>
              <Field label="UF">
                <select value={form.uf} onChange={(event) => update('uf', event.target.value)}>
                  <option value="">-</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          <section>
            <h4>Retirada</h4>
            <div className="registration-edit-grid">
              <Field label="Nome">
                <input value={form.nomeRetirada} onChange={(event) => update('nomeRetirada', event.target.value)} />
              </Field>
              <Field label="Parentesco">
                <input value={form.parentescoRetirada} onChange={(event) => update('parentescoRetirada', event.target.value)} />
              </Field>
            </div>
          </section>

          <section>
            <h4>Saúde e piscina</h4>
            <div className="registration-edit-grid">
              <Field label="Piscina" span>
                <select value={form.autorizacaoPiscina} onChange={(event) => update('autorizacaoPiscina', event.target.value)}>
                  <option value="autorizado">Autorizado</option>
                  <option value="somente_boia">Somente com boia</option>
                  <option value="nao_autorizado">Não autorizado</option>
                </select>
              </Field>
            </div>
            <div className="registration-edit-toggles">
              <Toggle label="Sabe nadar" checked={form.sabeNadar} onChange={(value) => update('sabeNadar', value)} />
              <Toggle label="Possui alergia" checked={form.possuiAlergia} onChange={(value) => update('possuiAlergia', value)} />
              <Toggle label="Possui doença" checked={form.possuiDoenca} onChange={(value) => update('possuiDoenca', value)} />
              <Toggle label="Toma medicamento" checked={form.tomaMedicamento} onChange={(value) => update('tomaMedicamento', value)} />
              <Toggle label="Medicação p/ hiperatividade" checked={form.medicacaoHiperatividade} onChange={(value) => update('medicacaoHiperatividade', value)} />
            </div>
            <div className="registration-edit-grid">
              {form.possuiAlergia && (
                <Field label="Quais alergias" span>
                  <input value={form.alergias} onChange={(event) => update('alergias', event.target.value)} />
                </Field>
              )}
              {form.possuiDoenca && (
                <Field label="Quais doenças" span>
                  <input value={form.doencas} onChange={(event) => update('doencas', event.target.value)} />
                </Field>
              )}
              {form.tomaMedicamento && (
                <Field label="Quais medicamentos" span>
                  <input value={form.medicamentos} onChange={(event) => update('medicamentos', event.target.value)} />
                </Field>
              )}
              {form.medicacaoHiperatividade && (
                <Field label="Medicamentos p/ hiperatividade" span>
                  <input value={form.medicamentosHiperatividade} onChange={(event) => update('medicamentosHiperatividade', event.target.value)} />
                </Field>
              )}
            </div>
          </section>

          <section>
            <h4>Adicionais e pagamento</h4>
            <div className="registration-edit-toggles">
              <Toggle label="Transporte van" checked={form.transporteVan} onChange={(value) => update('transporteVan', value)} />
              <Toggle label="Bingo" checked={form.participacaoBingo} onChange={(value) => update('participacaoBingo', value)} />
              <Toggle label="Gratuito" checked={form.gratuito} onChange={(value) => update('gratuito', value)} />
              <Toggle label="Pago" checked={form.pago} onChange={(value) => update('pago', value)} />
            </div>
            <div className="registration-edit-grid">
              <Field label="Status">
                <select value={form.status} onChange={(event) => update('status', event.target.value)}>
                  <option value="confirmado">Confirmado</option>
                  <option value="pendente">Pendente</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </Field>
              <Field label="Valor total (R$)">
                <input inputMode="decimal" value={form.valorTotal} onChange={(event) => update('valorTotal', event.target.value)} />
              </Field>
            </div>
          </section>
        </div>

        <div className="registration-edit-footer">
          {error ? <span className="registration-edit-error">{error}</span> : <span className="registration-edit-hint">{hasChanges ? 'Alterações pendentes' : 'Nenhuma alteração'}</span>}
          <div className="registration-edit-footer-actions">
            <button type="button" className="registration-edit-cancel" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="registration-edit-save" disabled={saving || !hasChanges}>
              <Save size={15} />
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegistrationEditModal;
